import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { orderBy } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildPopularStylesPath,
  FeatureSpecsEntities,
  FeatureSpecsPaths,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { buildSegmentRatingsPath, TypeRankingsFeaturesModel } from 'client/data/models/type-rankings-features';
import { EditorialReviewEntities } from 'client/data/models/editorial-review';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { PageModel } from 'client/data/models/page';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import {
  getAppraisalLink,
  getAppraisalLinkTextFromVehicle,
} from 'site-modules/shared/utils/appraisal-link-constructor';
import { stripHtml } from 'client/utils/string-utils';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getOverallRating, getOverallVerdict, getRatingScale } from 'site-modules/shared/utils/editorial-helper';
import { getBestSegmentRating } from 'site-modules/shared/utils/core-page/rating-and-ranking';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { TruncatedTextLite } from 'site-modules/shared/components/truncated-text-lite/truncated-text-lite';
import { hasExplanation, RatingBadge } from 'site-modules/core-page/components/rating-badge/rating-badge';
import { Link } from 'site-modules/shared/components/link/link';
import { CardsRow } from './cards-row/cards-row';

import './core-highlights.scss';

const CREATIVE_ID = 'edmunds-highlights';

function CoreHighlightsUI({
  children = null,
  className = null,
  params,
  vehicle,
  styles = null,
  editorialReview = null,
  sharedTrimName = null,
  segmentRating = null,
  stylesFeatures = null,
  isUsed = false,
  isMobile = false,
  noCardsRow = false,
}) {
  const isScorecardV4 = editorialReview?.isScorecardV4;
  const ratingVersion = editorialReview?.ratingsV4?.ratingVersion || editorialReview?.scorecardVersion;
  const editorialRating = getOverallRating(editorialReview);
  const editorialVerdict = getOverallVerdict(editorialReview);

  const ratingDisclaimer = editorialReview?.ratingDisclaimer;
  const isCarryoverRating = editorialReview?.isCarryoverRating;
  const ratingScale = getRatingScale(editorialReview);
  const {
    model: { name: modelName },
    year,
  } = vehicle;

  if ((noCardsRow && !editorialVerdict) || (!noCardsRow && !(styles && stylesFeatures))) {
    return null;
  }

  const cardsRow = !noCardsRow && (
    <Fragment>
      <CardsRow
        params={params}
        sharedTrimName={sharedTrimName}
        styles={styles}
        stylesFeatures={stylesFeatures}
        segmentRating={segmentRating}
        editorialVerdict={editorialVerdict}
        editorialRating={editorialRating}
        reviewEstimatedRange={editorialReview?.estimatedRange}
        ratingScale={ratingScale}
        creativeId={CREATIVE_ID}
        ratingVersion={ratingVersion}
        isScorecardV4={isScorecardV4}
        isUsed={isUsed}
      />
      {isUsed && (
        <div className="d-flex flex-column align-items-start mt-1">
          {!editorialVerdict && (
            <Button
              tag={ScrollLink}
              to={MENU_HASH.REVIEWS}
              scrollConfig={{ offset: SCROLL_OFFSET }}
              color="link"
              className="rounded medium mb-0_25 text-transform-none fw-normal text-cool-gray-40 px-0 py-0"
              data-tracking-id="view_edmunds_review"
            >
              Read full review
              <i className="icon-arrow-right3 text-primary-darker d-inline-block size-10 ms-0_25" aria-hidden />
            </Button>
          )}
          <Button
            tag={ScrollLink}
            to={MENU_HASH.FEATURES}
            scrollConfig={{ offset: SCROLL_OFFSET }}
            color="link"
            className="rounded medium text-transform-none fw-normal text-cool-gray-40 px-0 py-0"
            data-tracking-id="view_features_specs"
          >
            See all features & specs
            <i className="icon-arrow-right3 text-primary-darker d-inline-block size-10 ms-0_25" aria-hidden />
          </Button>
          {isMobile && (
            <Fragment>
              <Button
                tag={Link}
                to={getAppraisalLink(params)}
                data-tracking-id="tool_select_appraise_your_car"
                color="link"
                className="rounded medium mt-0_25 text-transform-none fw-normal text-cool-gray-40 px-0 py-0"
              >
                {getAppraisalLinkTextFromVehicle(vehicle)}
                <i className="icon-arrow-right3 text-primary-darker d-inline-block size-10 ms-0_25" aria-hidden />
              </Button>
              <Button
                tag={ScrollLink}
                to={MENU_HASH.FOR_SALE}
                color="blue-50"
                size="lg"
                className="w-100 size-16 rounded-8 fw-normal text-transform-none py-0_5 mt-1"
                data-tracking-id="view_shop_module"
              >
                See All for Sale
              </Button>
            </Fragment>
          )}
        </div>
      )}
    </Fragment>
  );

  const ratingDisclaimerFragment = !!(ratingDisclaimer || isCarryoverRating) && (
    <Fragment>
      {!isMobile && <hr className="mt-1 mb-0_75" />}
      {/* To not be crawled */}
      <style>
        {`
          .no-crawl::before {
            content: attr(data-content);
          }
        `}
      </style>
      <em
        className="no-crawl small me-0_25"
        data-content={stripHtml(
          ratingDisclaimer ||
            `Please note that this rating is based on a prior year and may change slightly once complete information about the ${year} ${modelName} becomes available.`
        )}
      />
    </Fragment>
  );

  const readFullReviewLink = !isUsed && (
    <ScrollLink
      to={MENU_HASH.REVIEWS}
      scrollConfig={{ offset: SCROLL_OFFSET }}
      className="rounded size-16 text-underline"
      data-tracking-id="view_edmunds_review"
    >
      Read full review
    </ScrollLink>
  );

  const truncatedVerdictProps = {
    className: classnames({ 'with-review-btn': !isUsed }),
    text: editorialVerdict,
    isHtmlText: true,
    additionalContent: [
      { content: readFullReviewLink },
      { content: ratingDisclaimerFragment, className: 'd-block mt-1 no-line-height' },
    ].filter(({ content }) => content),
    maxTextLength: 250,
    isInlineBtn: true,
    btnClassName: 'text-underline size-16',
    btnTextLess: 'less',
    btnTextMore: 'more',
    btnTextA11y: 'about this model',
    expandTrackingId: 'expand_verdict',
    expandTrackingValue: 'more',
  };

  return (
    <div className={classnames('core-highlights', className)} data-tracking-parent={CREATIVE_ID}>
      {!!editorialVerdict && (
        <div className="bg-white rounded-8 px-1 py-1">
          {children}
          <div className={classnames('d-md-flex align-items-md-center clearfix', { 'mb-1': !noCardsRow })}>
            <RatingBadge
              className="float-start float-md-none px-md-0_75 me-1"
              ratingScale={ratingScale}
              editorialRating={editorialRating}
              isScorecardV4={isScorecardV4}
              ratingVersion={ratingVersion}
              showExplanationTooltip={hasExplanation({ isUsed, year })}
              isScrollLink
            />
            <div>
              {isMobile ? (
                <TruncatedTextLite {...truncatedVerdictProps} />
              ) : (
                <Fragment>
                  <ContentFragment componentToUse="span" classes="me-0_25">
                    {editorialVerdict}
                  </ContentFragment>
                  {readFullReviewLink}
                  {!!ratingDisclaimerFragment && <div className="no-line-height">{ratingDisclaimerFragment}</div>}
                </Fragment>
              )}
              {isUsed && (
                <div className="mt-0_75">
                  <Button
                    tag={ScrollLink}
                    to={MENU_HASH.REVIEWS}
                    scrollConfig={{ offset: SCROLL_OFFSET }}
                    color="outline-primary-b"
                    className="rounded-8 size-16 px-1 py-0_25"
                    data-tracking-id="view_edmunds_review"
                  >
                    Read full review
                  </Button>
                </div>
              )}
            </div>
          </div>
          {cardsRow}
        </div>
      )}
      {!editorialVerdict && cardsRow}
    </div>
  );
}

CoreHighlightsUI.propTypes = {
  children: PropTypes.node,
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  className: PropTypes.string,
  styles: VehicleEntities.PopularStyles,
  sharedTrimName: PropTypes.string,
  editorialReview: EditorialReviewEntities.EditorialReview,
  segmentRating: TypeRankingsEntities.SegmentRating,
  stylesFeatures: PropTypes.arrayOf(FeatureSpecsEntities.FeatureStyleEntity),
  isUsed: PropTypes.bool,
  noCardsRow: PropTypes.bool,
  isMobile: PropTypes.bool,
  // used in getBestSegmentRating. Added here to avoid accidental removal
  submodelSlug: PropTypes.string,
};

export const CoreHighlights = connectToModel(CoreHighlightsUI, {
  sharedTrimName: bindToPath('sharedValues.trimName', PageModel),
  stylesFeatures: bindToPath(
    ({ vehicle, isPreprod }) => !isPreprod && FeatureSpecsPaths.buildPartialFeaturesPath(getParamsFromVehicle(vehicle)),
    VehicleModel
  ),
  styles: bindToPath(
    ({ vehicle, isPreprod }) => !isPreprod && buildPopularStylesPath(getParamsFromVehicle(vehicle)),
    VehicleModel,
    popularStyles => orderBy(popularStyles, 'price.baseMSRP')
  ),
  segmentRating: bindToPath(
    ({ params: { make, model, year }, isUsed, isPreprod }) =>
      !isUsed && !isPreprod && buildSegmentRatingsPath({ makeSlug: make, modelSlug: model, year }),
    TypeRankingsFeaturesModel,
    getBestSegmentRating
  ),
});
